// client/src/i18n/LanguageContext.js — Maxula
// P2-A (v1.156) : i18n FR/AR léger, SANS dépendance externe.
//   - t('clé', { vars }) avec interpolation {{var}}
//   - Repli automatique : ar manquant -> fr -> clé (jamais d'écran cassé)
//   - Bascule RTL globale : <html dir lang> + classe body "rtl" (police Cairo)
//   - Persistance : localStorage + user.preferredLanguage si connecté
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import fr from './translations/fr';
import ar from './translations/ar';

const DICTS = { fr, ar };
const STORAGE_KEY = 'maxula_lang';

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    const saved = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
    return saved === 'ar' ? 'ar' : 'fr';
  });

  // Applique direction + langue + police au document
  useEffect(() => {
    const rtl = lang === 'ar';
    document.documentElement.setAttribute('dir', rtl ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', lang);
    document.body.classList.toggle('rtl', rtl);
  }, [lang]);

  const setLang = useCallback((next) => {
    const l = next === 'ar' ? 'ar' : 'fr';
    setLangState(l);
    try { window.localStorage.setItem(STORAGE_KEY, l); } catch (e) {}
    // Persistance profil si connecté (non bloquant, import paresseux anti-cycle)
    try {
      const token = window.localStorage.getItem('token');
      if (token) {
        import('../services/api').then(({ userAPI }) => {
          userAPI.updateProfile({ preferredLanguage: l }).catch(() => {});
        }).catch(() => {});
      }
    } catch (e) {}
  }, []);

  // t('clé', { vars }) — repli ar -> fr -> clé
  const t = useCallback((key, vars) => {
    let str = (DICTS[lang] && DICTS[lang][key]);
    if (str == null) str = DICTS.fr[key];
    if (str == null) return key;
    if (vars) {
      for (const [k, v] of Object.entries(vars)) {
        str = str.split(`{{${k}}}`).join(String(v));
      }
    }
    return str;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, isRTL: lang === 'ar' }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage doit être utilisé dans LanguageProvider');
  return ctx;
};
